import {
    ComponentPropsWithoutRef,
    ReactNode,
    useEffect,
    useRef,
    useState,
    createContext,
    Dispatch,
    SetStateAction,
    useContext,
} from 'react';

import { throttle } from 'lodash';
import { Checkbox } from '@cimplico/components';

type TableProps = {
    children: ReactNode | ReactNode[];
    onCheck?: (checkedRows: string[]) => void;
};

type TableContextOptions = {
    headers: { pinned: boolean; offsetWidth: number; align: 'left' | 'right' | 'center' | 'justify' | 'char' }[];
    selectedRows: { rowId: string; checked: boolean }[];
    canSelect: boolean;
};

const TableContext = createContext<{ ctx: TableContextOptions; setCtx: Dispatch<SetStateAction<TableContextOptions>> }>({
    ctx: { headers: [], selectedRows: [], canSelect: false },
    setCtx: () => undefined,
});

export function Table({ children, onCheck }: TableProps): JSX.Element {
    const ref = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLTableElement>(null);
    const [scrollDirection, setScrollDirection] = useState<'left' | 'right' | 'both' | undefined>(undefined);
    const [ctx, setCtx] = useState<TableContextOptions>({ headers: [], selectedRows: [], canSelect: typeof onCheck === 'function' });

    useEffect(() => {
        const current = ref.current;
        if (!current) {
            return;
        }

        function handleShadows(el: EventTarget | null): void {
            if (el instanceof HTMLDivElement) {
                const scrollLeft = el.scrollLeft;

                const canScrollLeft = el.scrollLeft !== 0;
                const canScrollRight = el.scrollWidth - el.clientWidth !== Math.floor(el.scrollLeft);

                if (canScrollLeft && canScrollRight) {
                    setScrollDirection('both');
                    return;
                }

                if (canScrollLeft) {
                    setScrollDirection('left');
                    return;
                }

                if (canScrollRight) {
                    setScrollDirection('right');
                    return;
                }

                setScrollDirection(undefined);
            }
        }

        function scrollListener(ev: Event): void {
            handleShadows(ev.target);
        }

        handleShadows(current);

        current.addEventListener('scroll', throttle(scrollListener, 200));

        return (): void => {
            current.removeEventListener('scroll', throttle(scrollListener, 200));
        };
    }, [ctx.headers]);

    useEffect(() => {
        if (typeof onCheck === 'function') {
            onCheck(ctx.selectedRows.filter((sr) => sr.checked).map((sr) => sr.rowId));
        }
    }, [ctx.selectedRows, onCheck]);

    return (
        <div className="relative">
            <div className={`overflow-auto whitespace-nowrap flex `} ref={ref}>
                <div
                    className="z-20 bg-gradient-to-r from-gray-400/50 w-4 h-full top-0 absolute pointer-events-none"
                    style={{
                        left: 0,
                        visibility: scrollDirection === 'left' || scrollDirection === 'both' ? 'visible' : 'hidden',
                    }}
                ></div>
                <div className="inline-block min-w-full">
                    <TableContext.Provider value={{ ctx, setCtx }}>
                        <table className="min-w-full border-separate relative" cellSpacing={0} ref={tableRef}>
                            {children}
                        </table>
                    </TableContext.Provider>
                </div>
                <div
                    className="z-20 bg-gradient-to-l from-gray-400/50 w-4 h-full top-0 absolute pointer-events-none"
                    style={{
                        right: 0,
                        visibility: scrollDirection === 'right' || scrollDirection === 'both' ? 'visible' : 'hidden',
                    }}
                ></div>
            </div>
        </div>
    );
}

export function TableHead(props: ComponentPropsWithoutRef<'thead'>): JSX.Element {
    const { ctx, setCtx } = useContext(TableContext);

    function handleCheck(checked: boolean): void {
        setCtx((old) => {
            return {
                ...old,
                selectedRows: old.selectedRows.map((sr) => ({ rowId: sr.rowId, checked })),
            };
        });
    }

    return (
        <thead {...props}>
            <tr>
                {ctx.canSelect && (
                    <TableHeader cellIndex={0} pinned className="w-[50px]">
                        <Checkbox
                            checked={ctx.selectedRows?.every((sr) => sr.checked) && ctx.selectedRows?.length > 0}
                            onChange={(e): void => handleCheck(e.target.checked)}
                            indeterminate={ctx.selectedRows.some((sr) => sr.checked)}
                            tabIndex={-1}
                        />
                    </TableHeader>
                )}
                {props.children}
            </tr>
        </thead>
    );
}

export function TableHeader(props: ComponentPropsWithoutRef<'th'> & { cellIndex: number; pinned?: boolean }): JSX.Element {
    const { ctx, setCtx } = useContext(TableContext);
    const ref = useRef<HTMLTableCellElement>(null);

    useEffect(() => {
        if (!ref.current) {
            return;
        }

        const width = ref.current?.getBoundingClientRect().width;

        setCtx((ctx) => {
            ctx.headers[props.cellIndex] = {
                pinned: props.pinned ?? false,
                offsetWidth: props.pinned ? width : 0,
                align: props.align ?? 'left',
            };
            return ctx;
        });
    }, [props.align, props.cellIndex, props.pinned, setCtx]);

    // Get the stickOffset
    const stickyOffset = Math.ceil(ctx.headers.slice(0, props.cellIndex).reduce((acc, curr) => acc + curr.offsetWidth, 0));

    return (
        <th
            ref={ref}
            {...props}
            data-header={props.cellIndex}
            style={{
                position: 'sticky',
                zIndex: props.pinned ? 2 : 1,
                top: 0,
                left: props.pinned ? stickyOffset : undefined,
            }}
            className={`bg-white border-b border-r border-gray-200 px-4 py-2 text-sm text-gray-500 font-medium last:border-r-0 ${props.className}`}
        />
    );
}

export function TableBody(props: ComponentPropsWithoutRef<'tbody'>): JSX.Element {
    return <tbody {...props} />;
}

export function TableRow(props: ComponentPropsWithoutRef<'tr'> & { rowId?: string }): JSX.Element {
    const { ctx, setCtx } = useContext(TableContext);

    function handleCheck(checked: boolean): void {
        setCtx((old) => {
            return {
                ...old,
                selectedRows: old.selectedRows.map((sr) => ({ rowId: sr.rowId, checked: sr.rowId === props.rowId ? checked : sr.checked })),
            };
        });
    }

    useEffect(() => {
        const rowId = props.rowId;
        if (rowId) {
            setCtx((old) => {
                return {
                    ...old,
                    selectedRows: [...old.selectedRows, { rowId: rowId, checked: false }],
                };
            });

            return (): void => {
                setCtx((old) => {
                    return {
                        ...old,
                        selectedRows: [...old.selectedRows].filter((sr) => sr.rowId !== rowId),
                    };
                });
            };
        }
    }, [props.rowId, setCtx]);

    return (
        <tr {...props}>
            {ctx.canSelect && (
                <TableCell cellIndex={0} className="py-1 px-4 w-[50px]">
                    {props.rowId && (
                        <Checkbox
                            checked={ctx.selectedRows?.find((sr) => sr.rowId === props.rowId)?.checked}
                            onChange={(e): void => handleCheck(e.target.checked)}
                            tabIndex={-1}
                        />
                    )}
                </TableCell>
            )}

            {props.children}
        </tr>
    );
}

export function TableCell(props: ComponentPropsWithoutRef<'td'> & { cellIndex: number }): JSX.Element {
    // Get the stickOffset
    const { ctx } = useContext(TableContext);

    const stickyOffset = Math.ceil(ctx.headers.slice(0, props.cellIndex).reduce((acc, curr) => acc + curr.offsetWidth, 0));

    // Check if header is sticky
    const pinned = ctx.headers[props.cellIndex]?.pinned === true;
    return (
        <td
            {...props}
            data-cell={props.cellIndex}
            style={{
                position: pinned ? 'sticky' : undefined,
                zIndex: pinned ? 1 : undefined,
                top: pinned ? 0 : undefined,
                left: pinned ? stickyOffset : undefined,
            }}
            className={`border-b border-r last:border-r-0 border-gray-200  text-gray-800 text-sm bg-white  ${props.className}`}
            align={ctx.headers[props.cellIndex]?.align}
        />
    );
}

export function TableFooter(props: ComponentPropsWithoutRef<'tfoot'>): JSX.Element {
    return <tfoot {...props} />;
}
